import dayjs from 'dayjs';
import _ from 'lodash';
import type { ErrorFieldType, PayeurType } from '@/types';
import { isAdresseEmpty } from '../adresse';
import validateMail from './mail';
import validatePhone from './phone';

type ValidateFunctionType = (
  payeur: PayeurType,
  /** Données complémentaires à la fiche */
  otherData?: { nouveauCommentaire: string },
) => ErrorFieldType[];

/**
 * Valide un payeur
 *
 * @param payeur
 */
const validatePayeur: ValidateFunctionType = (payeur) => {
  let errors: ErrorFieldType[] = [];

  errors = [...errors, ...validateRequired(payeur)];

  // Si on a des champs obligatoires non renseignés, on ne va pas plus loin
  if (!_.isEmpty(errors)) return errors;

  errors = [...errors, ...validateFormat(payeur)];

  return errors;
};

/**
 * Valide les champs obligatoires suivant :
 *
 * nom, prenom, civilite, adresse,
 * dateNaissance, departementNaissance, villeNaissance, paysNaissance,
 * Au moins un des trois : telephone, portable, mail
 *
 * @param payeur
 */
const validateRequired = (payeur: PayeurType): ErrorFieldType[] => {
  const errors: ErrorFieldType[] = [];

  const { nom, prenom, civilite, adresse, telephone, portable, mail } = payeur;

  if (!civilite?.identifiant) {
    errors.push({ id: 'civilite', message: 'La civilité est obligatoire' });
  }
  if (_.isEmpty(nom)) {
    errors.push({ id: 'nom', message: 'Le nom est obligatoire' });
  }

  if (_.isEmpty(prenom)) {
    errors.push({ id: 'prenom', message: 'Le prénom est obligatoire' });
  }

  if (isAdresseEmpty(adresse)) {
    errors.push({ id: 'adresse', message: "L'adresse est obligatoire" });
  }

  if (_.isEmpty(telephone) && _.isEmpty(portable) && _.isEmpty(mail)) {
    errors.push({
      id: 'contact',
      message:
        'Au moins un des trois moyens de contact est obligatoire : téléphone, portable ou courriel',
    });
  }

  return errors;
};

/**
 * Valide le contenu de tous les champs du payeur.
 * Pars du principe qu'ils sont tous renseignés
 *
 * @param payeur
 */
const validateFormat: ValidateFunctionType = (payeur) => {
  let errors: ErrorFieldType[] = [];

  const {
    /** Champs obligatoires */
    nom,
    prenom,
    dateNaissance,
    departementNaissance,
    villeNaissance,
    paysNaissance,
    telephone,
    portable,
    mail,
    /** Champs facultatif */
    commentaire,
    nouveauCommentaire,
    /** Champs qui doivent juste pas être vide */
    // civilite,
    // adresse,
    /** Inutiles pour l'instant */
    // categoriePes,
    // identifiantTiers,
    // natureIdentifiant,
    // naturePes,
    // nomComplementaire,
    // referenceTiers,
  } = payeur;

  if (_.size(nom) > 250) {
    errors.push({ id: 'nom', message: 'Le nom ne doit pas dépasser 250 caractères' });
  }

  if (_.size(prenom) > 250) {
    errors.push({ id: 'prenom', message: 'Le prénom ne doit pas dépasser 250 caractères' });
  }

  if (_.size(commentaire) + _.size(nouveauCommentaire) > 1000) {
    errors.push({
      id: 'commentaire',
      message: 'Tous les commentaires cumulés ne doivent pas dépasser 1000 caractères',
    });
  }

  if (!dayjs(dateNaissance).isValid()) {
    errors.push({ id: 'dateNaissance', message: 'La date de naissance est invalide' });
  }

  if (_.size(villeNaissance) > 250) {
    errors.push({
      id: 'villeNaissance',
      message: 'La ville de naissance ne doit pas dépasser 250 caractères',
    });
  }

  if (_.size(departementNaissance) > 250) {
    errors.push({
      id: 'departementNaissance',
      message: 'Le département de naissance ne doit pas dépasser 250 caractères',
    });
  }

  if (_.size(paysNaissance) > 250) {
    errors.push({
      id: 'paysNaissance',
      message: 'Le pays de naissance ne doit pas dépasser 250 caractères',
    });
  }

  if (!_.isEmpty(telephone)) {
    const phoneErrors = validatePhone(telephone);
    if (!_.isEmpty(phoneErrors)) {
      _.forEach(phoneErrors, (error) => {
        errors.push({ id: 'telephone', message: `N° de téléphone : ${error.message}` });
      });
    }
  }

  if (!_.isEmpty(portable)) {
    const portableErrors = validatePhone(portable);
    if (!_.isEmpty(portableErrors)) {
      _.forEach(portableErrors, (error) => {
        errors.push({ id: 'portable', message: `N° de portable : ${error.message}` });
      });
    }
  }

  if (!_.isEmpty(mail)) {
    errors = [...errors, ...validateMail(mail)];
  }

  return errors;
};

export default validatePayeur;
